import type { PainPoint, SessionDetail, UseCase, UseCaseDetail } from "@ti/core";
import { useQueries, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, BadgeCheck, Frown, MessageSquareQuote, MessagesSquare, Network } from "lucide-react";
import { type ReactNode, useState } from "react";
import { FrictionChip, OutcomeChip, PainKindBadge, PointsDelta, SuccessRate } from "@/components/domain/analytics";
import { Sparkline } from "@/components/domain/charts";
import { AgentLink, PageHeader, TimeAgo } from "@/components/domain/misc";
import { DeltaPct } from "@/components/domain/score";
import { Page, QueryState } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { EmptyState, Skeleton } from "@/components/ui/misc";
import { MAGNITUDE_COLOR } from "@/features/agents/bits";
import { PtsDelta, Segmented } from "@/features/analytics/bits";
import { PATHS, sessionSearch } from "@/features/analytics/links";
import { OutcomeLegend, OutcomeMixChart } from "@/features/analytics/outcome-chart";
import { KpiTile } from "@/features/overview/kpi-tile";
import { PathFlow } from "@/features/paths/path-flow";
import { PathFindings } from "@/features/paths/path-lists";
import { OutcomeDefinitionsCard } from "@/features/use-cases/outcome-definitions";
import { type UseCaseAction, UseCaseDialogs, UseCaseMenu, UseCaseStatusBadge } from "@/features/use-cases/use-case-actions";
import { q, useUpdateUseCase } from "@/lib/api";
import { fmtCompact, fmtCost, fmtDuration, fmtInt, fmtMoney, fmtPct } from "@/lib/format";

export const Route = createFileRoute("/_app/use-cases/$useCaseId")({
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(q.useCase(params.useCaseId));
  },
  component: UseCasePage,
});

function UseCasePage() {
  const { useCaseId } = Route.useParams();
  const detail = useQuery(q.useCase(useCaseId));
  return (
    <Page wide>
      <QueryState
        query={detail}
        loading={
          <>
            <PageHeader breadcrumb={<Breadcrumb />} title={<Skeleton className="h-6 w-56" />} />
            <div className="grid grid-cols-2 gap-3 xl:grid-cols-6">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="h-[124px]" />
              ))}
            </div>
            <Skeleton className="mt-4 h-80" />
          </>
        }
      >
        {(u) => <UseCaseView u={u} />}
      </QueryState>
    </Page>
  );
}

function Breadcrumb({ parent, name }: { parent?: UseCase; name?: string }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5">
      <Link to="/use-cases" className="hover:text-fg hover:underline">
        Use cases
      </Link>
      {parent ? (
        <>
          <span aria-hidden>/</span>
          <Link to="/use-cases/$useCaseId" params={{ useCaseId: parent.id }} className="hover:text-fg hover:underline">
            {parent.name}
          </Link>
        </>
      ) : null}
      {name ? (
        <>
          <span aria-hidden>/</span>
          <span aria-current="page">{name}</span>
        </>
      ) : null}
    </nav>
  );
}

function UseCaseView({ u }: { u: UseCaseDetail }) {
  const all = useQuery(q.useCases());
  const agents = useQuery(q.agents());
  const navigate = useNavigate();
  const confirm = useUpdateUseCase(u.id);
  const [target, setTarget] = useState<{ action: UseCaseAction; useCase: UseCase } | null>(null);
  const parent = u.parentId ? all.data?.find((x) => x.id === u.parentId) : undefined;
  const children = all.data?.filter((x) => x.parentId === u.id) ?? [];
  const agentName = (id: string) => agents.data?.find((a) => a.id === id)?.name ?? id;

  return (
    <>
      <PageHeader
        breadcrumb={<Breadcrumb parent={parent} name={u.name} />}
        title={
          <span className="flex items-center gap-2">
            {u.name}
            <UseCaseStatusBadge status={u.status} />
          </span>
        }
        description={
          <>
            {u.description}{" "}
            <span className="text-fg-3">
              · {u.agentIds.map((a, i) => (
                <span key={a}>
                  {i ? ", " : ""}
                  <AgentLink id={a} name={agentName(a)} className="text-[12px]" />
                </span>
              ))}
            </span>
          </>
        }
        actions={
          <>
            {u.status === "discovered" ? (
              <Button variant="outline" disabled={confirm.isPending} onClick={() => confirm.mutate({ action: "confirm" })}>
                <BadgeCheck /> Confirm
              </Button>
            ) : null}
            <Button asChild>
              <Link to="/sessions" search={sessionSearch({ useCaseId: u.id })}>
                <MessagesSquare /> View sessions
              </Link>
            </Button>
            {all.data ? <UseCaseMenu useCase={u} all={all.data} onAction={(action) => setTarget({ action, useCase: u })} /> : null}
          </>
        }
      />

      <div className="flex flex-col gap-4">
        <Kpis u={u} />

        <div className="grid gap-4 lg:grid-cols-12">
          <OutcomeCard u={u} />
          <div className="flex flex-col gap-4 lg:col-span-5">
            <BreakdownCard
              title="By agent version"
              description="Success rate of this use case per version that served it."
              rows={u.byVersion.map((v) => ({ key: `${v.agentId}:${v.version}`, label: <span className="font-mono text-[12px]">{u.agentIds.length > 1 ? `${agentName(v.agentId)} ` : ""}{v.version}</span>, sessions: v.sessions, rate: v.successRate }))}
            />
            <BreakdownCard
              title="By segment"
              description="From your identify() traits."
              rows={u.bySegment.map((s) => ({ key: s.segment, label: s.segment, sessions: s.sessions, rate: s.successRate, href: s.segment }))}
              useCaseId={u.id}
            />
          </div>
        </div>

        <OutcomeDefinitionsCard useCaseId={u.id} />

        <Card>
          <CardHeader
            title={
              <span className="inline-flex items-center gap-1.5">
                <Network className="size-3.5 text-fg-3" aria-hidden /> Conversation paths
              </span>
            }
            description="Stages these sessions move through and where they end. Band width is volume; color is the success rate of sessions taking that transition."
            actions={
              <Link to="/paths" search={{ useCaseId: u.id, agentId: u.agentIds[0] }} className="inline-flex items-center gap-1 text-xs text-fg-3 hover:text-accent">
                Open in Paths <ArrowRight className="size-3" />
              </Link>
            }
          />
          <CardBody>
            <PathFlow paths={u.paths} title={`Conversation flow for ${u.name}`} />
          </CardBody>
        </Card>

        {u.paths.findings.length ? <PathFindings paths={u.paths} scope={{ useCaseId: u.id, agentId: u.agentIds[0] }} /> : null}

        <div className="grid gap-4 lg:grid-cols-12">
          <PainPointsCard ids={u.painPointIds} useCaseId={u.id} />
          <ExamplesCard u={u} />
        </div>

        {children.length ? <ChildrenCard items={children} /> : null}

        <SampleSessions u={u} onOpen={(id) => void navigate({ to: "/sessions/$sessionId", params: { sessionId: id } })} />
      </div>
      {all.data ? (
        <UseCaseDialogs
          target={target}
          all={all.data}
          onClose={() => setTarget(null)}
          onMerged={(intoId) => void navigate({ to: "/use-cases/$useCaseId", params: { useCaseId: intoId }, replace: true })}
        />
      ) : null}
    </>
  );
}

function Kpis({ u }: { u: UseCaseDetail }) {
  return (
    <section aria-label="Key metrics" className="@container">
      <div className="grid grid-cols-2 gap-3 @2xl:grid-cols-3 @5xl:grid-cols-6">
        <KpiTile
          label="Sessions · 7d"
          value={fmtCompact(u.sessions7d)}
          sub={
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <DeltaPct value={u.sessionsDeltaPct} /> · {fmtPct(u.share, 1)} of all sessions
            </span>
          }
          footer={<Sparkline data={u.trend} width={132} height={26} color={MAGNITUDE_COLOR} label={`${u.name} sessions per day, last ${u.trend.length} days`} />}
        />
        <KpiTile
          label="Success rate"
          value={<SuccessRate value={u.successRate} />}
          sub={
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <PtsDelta value={u.successRateDelta} /> vs previous 7 days
            </span>
          }
          footer={<Sparkline data={u.successTrend.map((v) => Math.round(v * 100))} width={132} height={26} color={MAGNITUDE_COLOR} label={`${u.name} daily success rate`} />}
        />
        <KpiTile
          label="Frustration rate"
          value={fmtPct(u.frustrationRate, 1)}
          sub={`end sentiment ${u.avgSentimentEnd > 0 ? "+" : ""}${u.avgSentimentEnd.toFixed(2)}`}
          footer={
            u.topFriction.length ? (
              <div className="flex flex-wrap gap-1">
                {u.topFriction.slice(0, 2).map((f) => (
                  <FrictionChip key={f.kind} kind={f.kind} />
                ))}
              </div>
            ) : null
          }
        />
        <KpiTile label="Turns · duration" value={u.avgTurns.toFixed(1)} sub={`avg ${fmtDuration(u.avgDurationMs)} per session`} />
        <KpiTile label="Cost per success" value={fmtCost(u.costPerSuccess)} sub={`${fmtCost(u.costPerSession)} per session`} />
        <KpiTile
          label="Third-party exposure · 30d"
          to="/activity"
          search={u.agentIds[0] ? { agent: u.agentIds[0] } : undefined}
          value={fmtMoney(u.exposure30d, { compact: true })}
          sub="value of actions agents took for end users in this use case"
        />
      </div>
    </section>
  );
}

function OutcomeCard({ u }: { u: UseCaseDetail }) {
  const outcomes = useQuery(q.outcomes({ useCaseId: u.id }));
  const [mode, setMode] = useState<"share" | "count">("share");
  return (
    <Card className="flex flex-col lg:col-span-7">
      <CardHeader
        title="Outcome mix over time"
        description="Daily outcomes for sessions in this use case."
        actions={
          <Segmented
            size="sm"
            label="Outcome mix scale"
            value={mode}
            onChange={setMode}
            options={[
              { value: "share", label: "Share" },
              { value: "count", label: "Sessions" },
            ]}
          />
        }
      />
      <CardBody className="flex flex-1 flex-col gap-3 pb-3">
        <QueryState query={outcomes} loading={<Skeleton className="h-[240px]" />}>
          {(o) => (
            <>
              <OutcomeMixChart daily={o.daily} mode={mode} height={240} />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <OutcomeLegend daily={o.daily} />
                <span className="text-[11px] text-fg-3 tabular">
                  source: {fmtPct(o.bySource.event / Math.max(1, o.bySource.event + o.bySource.business + o.bySource.inferred))} event ·{" "}
                  {fmtPct(o.bySource.business / Math.max(1, o.bySource.event + o.bySource.business + o.bySource.inferred))} business ·{" "}
                  {fmtPct(o.bySource.inferred / Math.max(1, o.bySource.event + o.bySource.business + o.bySource.inferred))} inferred
                </span>
              </div>
            </>
          )}
        </QueryState>
      </CardBody>
    </Card>
  );
}

function BreakdownCard({ title, description, rows, useCaseId }: { title: string; description: string; rows: Array<{ key: string; label: ReactNode; sessions: number; rate: number; href?: string }>; useCaseId?: string }) {
  const max = Math.max(1, ...rows.map((r) => r.sessions));
  return (
    <Card>
      <CardHeader title={title} description={description} />
      {rows.length ? (
        <table className="w-full text-[13px]">
          <caption className="sr-only">{title}: sessions and success rate</caption>
          <thead className="sr-only">
            <tr>
              <th scope="col">Group</th>
              <th scope="col">Sessions</th>
              <th scope="col">Success rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b last:border-0">
                <td className="w-32 px-4 py-2 text-fg">
                  {r.href && useCaseId ? (
                    <Link to="/sessions" search={sessionSearch({ useCaseId, segment: r.href })} className="hover:text-accent hover:underline">
                      {r.label}
                    </Link>
                  ) : (
                    r.label
                  )}
                </td>
                <td className="px-2 py-2">
                  <span className="flex items-center gap-2">
                    <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-3" aria-hidden>
                      <span className="block h-full rounded-full" style={{ width: `${(r.sessions / max) * 100}%`, background: MAGNITUDE_COLOR }} />
                    </span>
                    <span className="w-14 text-right text-xs tabular text-fg-2">{fmtCompact(r.sessions)}</span>
                  </span>
                </td>
                <td className="w-20 px-4 py-2 text-right">
                  <SuccessRate value={r.rate} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState className="py-6" title="No data yet" />
      )}
    </Card>
  );
}

function PainPointsCard({ ids, useCaseId }: { ids: string[]; useCaseId: string }) {
  const pains = useQuery(q.painPoints());
  const linked: PainPoint[] = (pains.data ?? []).filter((p) => ids.includes(p.id) || p.useCaseIds.includes(useCaseId));
  return (
    <Card className="lg:col-span-7">
      <CardHeader
        title="Linked pain points"
        description="Recurring reasons sessions in this use case go badly."
        actions={
          <Link to={PATHS.painPoints} className="inline-flex items-center gap-1 text-xs text-fg-3 hover:text-accent">
            All pain points <ArrowRight className="size-3" />
          </Link>
        }
      />
      <QueryState query={pains} loading={<Skeleton className="m-4 h-32" />}>
        {() =>
          linked.length ? (
            <ul className="flex flex-col">
              {linked.map((p) => (
                <li key={p.id} className="flex flex-col gap-1.5 px-4 py-3 [&:not(:last-child)]:border-b">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-[13px] font-medium text-fg">{p.title}</span>
                    <PainKindBadge kind={p.kind} />
                  </div>
                  <p className="text-xs text-fg-2">{p.suggestion}</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-3">
                    <span className="tabular">
                      <span className="text-fg-2">{fmtInt(p.sessions7d)}</span> sessions · <DeltaPct value={p.deltaPct} invert />
                    </span>
                    <span>
                      success impact <PointsDelta value={p.successImpact} />
                    </span>
                    <span className="tabular">{fmtMoney(p.costImpact7d, { compact: true })} cost / 7d</span>
                    <Link to="/sessions" search={sessionSearch({ painPointId: p.id })} className="ml-auto hover:text-accent hover:underline">
                      Sessions
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<Frown />} title="No pain points linked" description="Nothing recurring stands out for this use case." />
          )
        }
      </QueryState>
    </Card>
  );
}

function ExamplesCard({ u }: { u: UseCaseDetail }) {
  return (
    <Card className="lg:col-span-5">
      <CardHeader title="Example intents" description="Picked by discovery to describe the cluster, in users' words." />
      {u.examples.length ? (
        <ul className="flex flex-col">
          {u.examples.map((e) => (
            <li key={e} className="flex items-start gap-2 px-4 py-2.5 text-[13px] text-fg [&:not(:last-child)]:border-b">
              <MessageSquareQuote className="mt-0.5 size-3.5 shrink-0 text-fg-3" aria-hidden />“{e}”
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState className="py-6" title="No examples" />
      )}
      {u.topFriction.length ? (
        <div className="flex flex-col gap-1.5 border-t px-4 py-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-fg-3">Top friction</span>
          <ul className="flex flex-col gap-1">
            {u.topFriction.map((f) => (
              <li key={f.kind} className="flex items-center justify-between gap-2 text-xs">
                <Link to="/sessions" search={sessionSearch({ useCaseId: u.id, friction: f.kind })} className="hover:opacity-80">
                  <FrictionChip kind={f.kind} />
                </Link>
                <span className="tabular text-fg-2">{fmtInt(f.sessions)} sessions</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}

function ChildrenCard({ items }: { items: UseCase[] }) {
  return (
    <Card>
      <CardHeader title="Sub-cases" description={`${items.length} use cases under this one.`} />
      <ul className="grid gap-px bg-border sm:grid-cols-2 xl:grid-cols-4">
        {items.map((c) => (
          <li key={c.id} className="bg-surface">
            <Link to="/use-cases/$useCaseId" params={{ useCaseId: c.id }} className="flex flex-col gap-1 px-4 py-3 hover:bg-surface-2">
              <span className="truncate text-[13px] font-medium text-fg">{c.name}</span>
              <span className="flex items-center gap-3 text-xs text-fg-3 tabular">
                <span>
                  <span className="text-fg-2">{fmtCompact(c.sessions7d)}</span> sessions
                </span>
                <span>
                  <SuccessRate value={c.successRate} /> success
                </span>
                <UseCaseStatusBadge status={c.status} />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function SampleSessions({ u, onOpen }: { u: UseCaseDetail; onOpen: (id: string) => void }) {
  const sessions = useQueries({ queries: u.sampleSessionIds.map((id) => q.session(id)) });
  const loaded = sessions.flatMap((s) => (s.data ? [s.data] : [])) as SessionDetail[];
  const pending = sessions.some((s) => s.isPending);
  return (
    <Card>
      <CardHeader
        title="Sample sessions"
        description="Representative sessions picked across outcomes."
        actions={
          <Link to="/sessions" search={sessionSearch({ useCaseId: u.id })} className="inline-flex items-center gap-1 text-xs text-fg-3 hover:text-accent">
            All sessions <ArrowRight className="size-3" />
          </Link>
        }
      />
      {pending && !loaded.length ? (
        <Skeleton className="m-4 h-40" />
      ) : loaded.length ? (
        <ul className="flex flex-col">
          {loaded.map((s) => (
            <li key={s.id} className="[&:not(:last-child)]:border-b">
              <button type="button" onClick={() => onOpen(s.id)} className="grid w-full cursor-pointer grid-cols-[120px_minmax(0,1fr)_auto] items-center gap-3 px-4 py-2.5 text-left hover:bg-surface-2">
                <OutcomeChip outcome={s.outcome} source={s.outcomeSource} />
                <span className="min-w-0">
                  <span className="block truncate text-[13px] text-fg">{s.intent || s.id}</span>
                  <span className="block truncate text-xs text-fg-3">{s.summary}</span>
                </span>
                <span className="flex items-center gap-3 text-xs text-fg-3">
                  {s.friction.slice(0, 1).map((f) => (
                    <FrictionChip key={f} kind={f} />
                  ))}
                  <span className="font-mono text-[11px]">{s.agentVersion}</span>
                  <TimeAgo iso={s.startedAt} className="text-fg-3" />
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon={<MessagesSquare />} title="No sample sessions" />
      )}
    </Card>
  );
}
